import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { Download, Save, Settings2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  HandsontableGrid,
  type HandsontableGridColumn,
} from "@/features/workbook/components/handsontable-grid";
import {
  WorkbookSidebar,
} from "@/features/workbook/components/workbook-sidebar";
import {
  defaultWorkbookSections,
  type WorkbookEquipmentRowData,
  type WorkbookSwitchPortRowData,
  type WorkbookVlanLegendRowData,
  toWorkbookSummary,
} from "@/features/workbook/mock-data";
import { ApiError } from "@/api/client";
import { toast } from "sonner";
import { useWorkbookStore } from "@/stores/workbook";
import { validateWorkbookSectionsForSave } from "@/features/workbook/pages/edit-validation";

type IspTab = "metadata" | "general" | "network" | "router";

const TABLE_HEIGHT = 520;
const MIN_ROWS = 20;

function triggerDownload(downloadUrl: string): void {
  const absoluteUrl = new URL(downloadUrl, window.location.origin).toString();
  const anchor = document.createElement("a");
  anchor.href = absoluteUrl;
  anchor.download = "";
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function newEquipmentRow(): WorkbookEquipmentRowData {
  return {
    index: "",
    type: "",
    category: "",
    vendor: "",
    model: "",
    ipAddress: "",
    lanMacAddress: "",
    serialNumber: "",
    name: "",
    location: "",
    deviceMode: "",
    notes: "",
  };
}

function newSwitchPortRow(): WorkbookSwitchPortRowData {
  return {
    switchName: "",
    port: "",
    linkLocation: "",
    nativeVlan: "",
    allowedVlans: "",
    description: "",
  };
}

function newVlanLegendRow(): WorkbookVlanLegendRowData {
  return {
    section: "",
    vlanId: "",
    vlanName: "",
    subnet: "",
    defaultGateway: "",
  };
}

function padRows<T>(rows: T[], factory: () => T, min = MIN_ROWS): T[] {
  if (rows.length >= min) return rows;
  return [...rows, ...Array.from({ length: min - rows.length }, factory)];
}

function trimTrailingEmptyRows<T extends Record<string, string>>(rows: T[]): T[] {
  const result = [...rows];
  while (result.length > 0 && !Object.values(result[result.length - 1]!).some(Boolean)) {
    result.pop();
  }
  return result;
}

const EQUIPMENT_COLUMNS: HandsontableGridColumn<WorkbookEquipmentRowData>[] = [
  { key: "index", title: "Index", width: 80 },
  { key: "type", title: "Type", width: 120 },
  { key: "category", title: "Category", width: 120 },
  { key: "vendor", title: "Vendor", width: 120 },
  { key: "model", title: "Model", width: 140 },
  { key: "ipAddress", title: "IP Address", width: 140 },
  { key: "lanMacAddress", title: "LAN MAC", width: 160 },
  { key: "serialNumber", title: "Serial Number", width: 160 },
  { key: "name", title: "Name", width: 140 },
  { key: "location", title: "Location", width: 160 },
  { key: "deviceMode", title: "Device Mode", width: 140 },
  { key: "notes", title: "Notes", width: 220 },
];

const SWITCH_PORT_COLUMNS: HandsontableGridColumn<WorkbookSwitchPortRowData>[] =
  [
    { key: "switchName", title: "Switch", width: 160 },
    { key: "port", title: "Port", width: 100 },
    { key: "linkLocation", title: "Link Location", width: 180 },
    { key: "nativeVlan", title: "Native VLAN", width: 120 },
    { key: "allowedVlans", title: "Allowed VLANs", width: 160 },
    { key: "description", title: "Description", width: 240 },
  ];

const VLAN_LEGEND_COLUMNS: HandsontableGridColumn<WorkbookVlanLegendRowData>[] =
  [
    { key: "section", title: "Section", width: 140 },
    { key: "vlanId", title: "VLAN ID", width: 120 },
    { key: "vlanName", title: "VLAN Name", width: 180 },
    { key: "subnet", title: "Subnet", width: 180 },
    { key: "defaultGateway", title: "Default Gateway", width: 180 },
  ];

function useWorkbookEditForm() {
  const { workbookId } = useParams();
  const [searchParams] = useSearchParams();
  const lastLoadKeyRef = useRef<string | null>(null);
  const record = useWorkbookStore((state) =>
    workbookId
      ? (state.records.find((item) => item.id === workbookId) ?? null)
      : null,
  );
  const saveSnapshot = useWorkbookStore((state) => state.saveSnapshot);
  const rollbackToSnapshot = useWorkbookStore(
    (state) => state.rollbackToSnapshot,
  );
  const compareSnapshots = useWorkbookStore((state) => state.compareSnapshots);
  const exportWorkbook = useWorkbookStore((state) => state.exportWorkbook);

  const workbook = useMemo(
    () => (record ? toWorkbookSummary(record) : null),
    [record],
  );
  const activeSnapshot = useMemo(
    () =>
      record
        ? (record.snapshots.find(
            (item) => item.id === record.activeSnapshotId,
          ) ??
          record.snapshots[record.snapshots.length - 1] ??
          null)
        : null,
    [record],
  );

  const created = searchParams.get("created") === "1";

  const [workbookName, setWorkbookName] = useState("");
  const [propertyName, setPropertyName] = useState("");
  const [status, setStatus] = useState("");
  const [author, setAuthor] = useState("");
  const [description, setDescription] = useState("");
  const [hotelDescription, setHotelDescription] = useState("");
  const [ispProvider, setIspProvider] = useState("");
  const [ispCircuitId, setIspCircuitId] = useState("");
  const [ispAccountNumber, setIspAccountNumber] = useState("");
  const [ispContact, setIspContact] = useState("");
  const [ispSla, setIspSla] = useState("");
  const [ispHandoffType, setIspHandoffType] = useState("");
  const [ispEscalation, setIspEscalation] = useState("");
  const [ispWanCidr, setIspWanCidr] = useState("");
  const [ispGateway, setIspGateway] = useState("");
  const [ispPrimaryDns, setIspPrimaryDns] = useState("");
  const [ispSecondaryDns, setIspSecondaryDns] = useState("");
  const [ispRouterModel, setIspRouterModel] = useState("");
  const [ispLanHandoff, setIspLanHandoff] = useState("");
  const [ispWanInterface, setIspWanInterface] = useState("");
  const [ispRouterNotes, setIspRouterNotes] = useState("");
  const [equipmentNotes, setEquipmentNotes] = useState("");
  const [switchPortMapNotes, setSwitchPortMapNotes] = useState("");
  const [vlanLegendNotes, setVlanLegendNotes] = useState("");
  const [equipmentList, setEquipmentList] = useState<
    WorkbookEquipmentRowData[]
  >([]);
  const [switchPortMap, setSwitchPortMap] = useState<
    WorkbookSwitchPortRowData[]
  >([]);
  const [vlanLegend, setVlanLegend] = useState<WorkbookVlanLegendRowData[]>([]);
  const [activeIspTab, setActiveIspTab] = useState<IspTab>("metadata");
  const [selectedSnapshotId, setSelectedSnapshotId] = useState("");
  const [rollbackConfirmed, setRollbackConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const loadKey = workbookId ?? "__workbook-edit-root__";
    if (lastLoadKeyRef.current === loadKey) {
      return;
    }
    lastLoadKeyRef.current = loadKey;
    void useWorkbookStore.getState().load();
  }, [workbookId]);

  // Show a toast once when arriving from the create flow.
  const createdToastFiredRef = useRef(false);
  useEffect(() => {
    if (created && !createdToastFiredRef.current) {
      createdToastFiredRef.current = true;
      toast.success("Workbook created", {
        description:
          "A first snapshot was recorded. Save metadata to generate additional revisions.",
      });
    }
  }, [created]);

  // Sync form fields when the loaded workbook or its active snapshot changes.
  const serverKey = record ? `${record.id}:${record.activeSnapshotId ?? ""}` : "";
  const [prevServerKey, setPrevServerKey] = useState("");
  if (serverKey !== prevServerKey) {
    setPrevServerKey(serverKey);
    if (workbook && record) {
      const sections = activeSnapshot?.sections ?? defaultWorkbookSections();
      setWorkbookName(workbook.workbookName);
      setPropertyName(workbook.propertyName);
      setStatus(workbook.status);
      setAuthor(workbook.author);
      setDescription(workbook.description);
      setHotelDescription(sections.hotelDescription);
      setIspProvider(sections.isp.metadata.provider);
      setIspCircuitId(sections.isp.metadata.circuitId);
      setIspAccountNumber(sections.isp.metadata.accountNumber);
      setIspContact(sections.isp.metadata.contact);
      setIspSla(sections.isp.general.sla);
      setIspHandoffType(sections.isp.general.handoffType);
      setIspEscalation(sections.isp.general.escalation);
      setIspWanCidr(sections.isp.network.wanCidr);
      setIspGateway(sections.isp.network.gateway);
      setIspPrimaryDns(sections.isp.network.primaryDns);
      setIspSecondaryDns(sections.isp.network.secondaryDns);
      setIspRouterModel(sections.isp.router.model);
      setIspLanHandoff(sections.isp.router.lanHandoff);
      setIspWanInterface(sections.isp.router.wanInterface);
      setIspRouterNotes(sections.isp.router.notes);
      setEquipmentNotes(sections.equipmentNotes);
      setSwitchPortMapNotes(sections.switchPortMapNotes);
      setVlanLegendNotes(sections.vlanLegendNotes);
      setEquipmentList(sections.equipmentList);
      setSwitchPortMap(sections.switchPortMap);
      setVlanLegend(sections.vlanLegend);
      setSelectedSnapshotId(record.activeSnapshotId);
      setRollbackConfirmed(false);
      setValidationMessages([]);
    }
  }

  const hasChanges = useMemo(() => {
    if (!workbook) {
      return false;
    }
    return (
      workbookName !== workbook.workbookName ||
      propertyName !== workbook.propertyName ||
      status !== workbook.status ||
      author !== workbook.author ||
      description !== workbook.description ||
      hotelDescription !== (activeSnapshot?.sections.hotelDescription ?? "") ||
      ispProvider !== (activeSnapshot?.sections.isp.metadata.provider ?? "") ||
      ispCircuitId !==
        (activeSnapshot?.sections.isp.metadata.circuitId ?? "") ||
      ispAccountNumber !==
        (activeSnapshot?.sections.isp.metadata.accountNumber ?? "") ||
      ispContact !== (activeSnapshot?.sections.isp.metadata.contact ?? "") ||
      ispSla !== (activeSnapshot?.sections.isp.general.sla ?? "") ||
      ispHandoffType !==
        (activeSnapshot?.sections.isp.general.handoffType ?? "") ||
      ispEscalation !==
        (activeSnapshot?.sections.isp.general.escalation ?? "") ||
      ispWanCidr !== (activeSnapshot?.sections.isp.network.wanCidr ?? "") ||
      ispGateway !== (activeSnapshot?.sections.isp.network.gateway ?? "") ||
      ispPrimaryDns !==
        (activeSnapshot?.sections.isp.network.primaryDns ?? "") ||
      ispSecondaryDns !==
        (activeSnapshot?.sections.isp.network.secondaryDns ?? "") ||
      ispRouterModel !== (activeSnapshot?.sections.isp.router.model ?? "") ||
      ispLanHandoff !==
        (activeSnapshot?.sections.isp.router.lanHandoff ?? "") ||
      ispWanInterface !==
        (activeSnapshot?.sections.isp.router.wanInterface ?? "") ||
      ispRouterNotes !== (activeSnapshot?.sections.isp.router.notes ?? "") ||
      equipmentNotes !== (activeSnapshot?.sections.equipmentNotes ?? "") ||
      switchPortMapNotes !==
        (activeSnapshot?.sections.switchPortMapNotes ?? "") ||
      vlanLegendNotes !== (activeSnapshot?.sections.vlanLegendNotes ?? "") ||
      JSON.stringify(equipmentList) !==
        JSON.stringify(activeSnapshot?.sections.equipmentList ?? []) ||
      JSON.stringify(switchPortMap) !==
        JSON.stringify(activeSnapshot?.sections.switchPortMap ?? []) ||
      JSON.stringify(vlanLegend) !==
        JSON.stringify(activeSnapshot?.sections.vlanLegend ?? [])
    );
  }, [
    activeSnapshot,
    author,
    description,
    equipmentNotes,
    hotelDescription,
    ispAccountNumber,
    ispCircuitId,
    ispContact,
    ispEscalation,
    ispGateway,
    ispHandoffType,
    ispLanHandoff,
    ispPrimaryDns,
    ispProvider,
    ispRouterModel,
    ispRouterNotes,
    ispSecondaryDns,
    ispSla,
    ispWanCidr,
    ispWanInterface,
    propertyName,
    status,
    switchPortMapNotes,
    switchPortMap,
    equipmentList,
    vlanLegend,
    vlanLegendNotes,
    workbook,
    workbookName,
  ]);

  const rollbackDiff = useMemo(() => {
    if (!workbookId || !record || !selectedSnapshotId) {
      return [];
    }
    return compareSnapshots(
      workbookId,
      record.activeSnapshotId,
      selectedSnapshotId,
    );
  }, [compareSnapshots, record, selectedSnapshotId, workbookId]);

  const validationErrorCountBySection = useMemo(
    () => ({
      equipment: validationMessages.filter((message) =>
        message.startsWith("Equipment row"),
      ).length,
      switchPortMap: validationMessages.filter((message) =>
        message.startsWith("Switch Port row"),
      ).length,
      vlanLegend: validationMessages.filter((message) =>
        message.startsWith("VLAN row"),
      ).length,
    }),
    [validationMessages],
  );

  async function onSaveSnapshot() {
    if (!workbookId || !workbook || !hasChanges) {
      return;
    }

    const validationErrors = validateWorkbookSectionsForSave({
      equipmentList,
      switchPortMap,
      vlanLegend,
    });
    if (validationErrors.length > 0) {
      setValidationMessages(validationErrors);
      const previewErrors = validationErrors.slice(0, 2).join(" ");
      const extraCount = validationErrors.length - 2;
      const suffix =
        extraCount > 0
          ? ` (+${extraCount} more validation issue${extraCount === 1 ? "" : "s"})`
          : "";
      toast.error("Validation failed", {
        description: `${previewErrors}${suffix}`,
      });
      return;
    }

    setValidationMessages([]);

    setSaving(true);
    try {
      const snapshotId = await saveSnapshot(
        workbookId,
        {
          workbookName,
          propertyName,
          status,
          author,
          accessLevel: workbook.accessLevel,
          description,
          sections: {
            hotelDescription,
            isp: {
              metadata: {
                provider: ispProvider,
                circuitId: ispCircuitId,
                accountNumber: ispAccountNumber,
                contact: ispContact,
              },
              general: {
                sla: ispSla,
                handoffType: ispHandoffType,
                escalation: ispEscalation,
              },
              network: {
                wanCidr: ispWanCidr,
                gateway: ispGateway,
                primaryDns: ispPrimaryDns,
                secondaryDns: ispSecondaryDns,
              },
              router: {
                model: ispRouterModel,
                lanHandoff: ispLanHandoff,
                wanInterface: ispWanInterface,
                notes: ispRouterNotes,
              },
            },
            equipmentNotes,
            switchPortMapNotes,
            vlanLegendNotes,
            equipmentList,
            switchPortMap,
            vlanLegend,
          },
        },
        "Current User",
        "Manual metadata update",
      );
      if (!snapshotId) {
        toast.error("Unable to save snapshot");
        return;
      }
      toast.success("Snapshot saved", {
        description: `New revision ${snapshotId} has been added to history.`,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Unable to save snapshot", { description: message });
    } finally {
      setSaving(false);
    }
  }

  async function onExportWorkbook() {
    if (!workbookId) {
      return;
    }
    setExporting(true);
    try {
      const result = await exportWorkbook(workbookId, "xlsx");
      if (!result) {
        toast.error("Export failed", {
          description: "The backend export task could not be started.",
        });
        return;
      }
      triggerDownload(result.downloadUrl);
      toast.success("Workbook export ready", {
        description: `Task ${result.taskId} completed. Download started.`,
      });
    } finally {
      setExporting(false);
    }
  }

  async function onRollback() {
    if (!workbookId || !selectedSnapshotId || !record) {
      return;
    }
    if (selectedSnapshotId === record.activeSnapshotId) {
      toast.info("Selected snapshot is already active");
      return;
    }
    if (!rollbackConfirmed) {
      toast.info("Confirm rollback before applying");
      return;
    }
    setRollingBack(true);
    try {
      const snapshotId = await rollbackToSnapshot(
        workbookId,
        selectedSnapshotId,
        "Current User",
      );
      if (!snapshotId) {
        toast.error("Rollback failed", {
          description: "Selected snapshot was not found.",
        });
        return;
      }
      toast.success("Rollback created", {
        description: `Active revision switched via ${snapshotId}.`,
      });
      setRollbackConfirmed(false);
    } finally {
      setRollingBack(false);
    }
  }

  return {
    workbook,
    activeSnapshot,
    workbookId,
    record,
    workbookName,
    setWorkbookName,
    propertyName,
    setPropertyName,
    status,
    setStatus,
    author,
    setAuthor,
    description,
    setDescription,
    hotelDescription,
    setHotelDescription,
    ispProvider,
    setIspProvider,
    ispCircuitId,
    setIspCircuitId,
    ispAccountNumber,
    setIspAccountNumber,
    ispContact,
    setIspContact,
    ispSla,
    setIspSla,
    ispHandoffType,
    setIspHandoffType,
    ispEscalation,
    setIspEscalation,
    ispWanCidr,
    setIspWanCidr,
    ispGateway,
    setIspGateway,
    ispPrimaryDns,
    setIspPrimaryDns,
    ispSecondaryDns,
    setIspSecondaryDns,
    ispRouterModel,
    setIspRouterModel,
    ispLanHandoff,
    setIspLanHandoff,
    ispWanInterface,
    setIspWanInterface,
    ispRouterNotes,
    setIspRouterNotes,
    equipmentNotes,
    setEquipmentNotes,
    switchPortMapNotes,
    setSwitchPortMapNotes,
    vlanLegendNotes,
    setVlanLegendNotes,
    equipmentList,
    setEquipmentList,
    switchPortMap,
    setSwitchPortMap,
    vlanLegend,
    setVlanLegend,
    activeIspTab,
    setActiveIspTab,
    selectedSnapshotId,
    setSelectedSnapshotId,
    rollbackConfirmed,
    setRollbackConfirmed,
    saving,
    rollingBack,
    exporting,
    validationMessages,
    sidebarOpen,
    setSidebarOpen,
    hasChanges,
    rollbackDiff,
    validationErrorCountBySection,
    onSaveSnapshot,
    onExportWorkbook,
    onRollback,
  };
}

type WorkbookEditForm = ReturnType<typeof useWorkbookEditForm>;

function WorkbookIspTab({ f }: { f: WorkbookEditForm }) {
  return (
    <>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="ISP sections">
        {(
          [
            ["metadata", "Metadata"],
            ["general", "General"],
            ["network", "Network"],
            ["router", "Router"],
          ] as const
        ).map(([tabKey, tabLabel]) => (
          <Button
            key={tabKey}
            type="button"
            size="sm"
            variant={f.activeIspTab === tabKey ? "primary" : "ghost"}
            role="tab"
            aria-selected={f.activeIspTab === tabKey}
            aria-controls={`isp-panel-${tabKey}`}
            id={`isp-tab-${tabKey}`}
            onClick={() => f.setActiveIspTab(tabKey)}
          >
            {tabLabel}
          </Button>
        ))}
      </div>

      <div id="isp-panel-metadata" role="tabpanel" aria-labelledby="isp-tab-metadata"
        hidden={f.activeIspTab !== "metadata"} className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-provider">Provider</Label>
          <Input id="isp-provider" value={f.ispProvider} onChange={(e) => f.setIspProvider(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-circuit-id">Circuit ID</Label>
          <Input id="isp-circuit-id" value={f.ispCircuitId} onChange={(e) => f.setIspCircuitId(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-account-number">Account Number</Label>
          <Input id="isp-account-number" value={f.ispAccountNumber} onChange={(e) => f.setIspAccountNumber(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-contact">Contact</Label>
          <Input id="isp-contact" value={f.ispContact} onChange={(e) => f.setIspContact(e.target.value)} />
        </div>
      </div>

      <div id="isp-panel-general" role="tabpanel" aria-labelledby="isp-tab-general"
        hidden={f.activeIspTab !== "general"} className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-sla">SLA</Label>
          <Input id="isp-sla" value={f.ispSla} onChange={(e) => f.setIspSla(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-handoff-type">Handoff Type</Label>
          <Input id="isp-handoff-type" value={f.ispHandoffType} onChange={(e) => f.setIspHandoffType(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-escalation">Escalation</Label>
          <Input id="isp-escalation" value={f.ispEscalation} onChange={(e) => f.setIspEscalation(e.target.value)} />
        </div>
      </div>

      <div id="isp-panel-network" role="tabpanel" aria-labelledby="isp-tab-network"
        hidden={f.activeIspTab !== "network"} className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-wan-cidr">WAN CIDR</Label>
          <Input id="isp-wan-cidr" value={f.ispWanCidr} onChange={(e) => f.setIspWanCidr(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-gateway">Gateway</Label>
          <Input id="isp-gateway" value={f.ispGateway} onChange={(e) => f.setIspGateway(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-primary-dns">Primary DNS</Label>
          <Input id="isp-primary-dns" value={f.ispPrimaryDns} onChange={(e) => f.setIspPrimaryDns(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-secondary-dns">Secondary DNS</Label>
          <Input id="isp-secondary-dns" value={f.ispSecondaryDns} onChange={(e) => f.setIspSecondaryDns(e.target.value)} />
        </div>
      </div>

      <div id="isp-panel-router" role="tabpanel" aria-labelledby="isp-tab-router"
        hidden={f.activeIspTab !== "router"} className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-router-model">Router Model</Label>
          <Input id="isp-router-model" value={f.ispRouterModel} onChange={(e) => f.setIspRouterModel(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-lan-handoff">LAN Handoff</Label>
          <Input id="isp-lan-handoff" value={f.ispLanHandoff} onChange={(e) => f.setIspLanHandoff(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="isp-wan-interface">WAN Interface</Label>
          <Input id="isp-wan-interface" value={f.ispWanInterface} onChange={(e) => f.setIspWanInterface(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <Label htmlFor="isp-router-notes">Router Notes</Label>
          <textarea id="isp-router-notes" aria-label="Router Notes" value={f.ispRouterNotes}
            onChange={(e) => f.setIspRouterNotes(e.target.value)}
            className="min-h-[110px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
          />
        </div>
      </div>
    </>
  );
}

export function WorkbookEditPage() {
  const f = useWorkbookEditForm();

  if (!f.workbook || !f.record || !f.workbookId) {
    return (
      <div className="px-6 py-8 text-sm text-fg-mute">
        Workbook not found — this workbook may have been removed or the URL is
        invalid.
      </div>
    );
  }

  const { workbook: wb, record, activeSnapshot } = f;

  return (
    <div className="flex h-full flex-col">
      {/* Slim top bar */}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Button asChild size="sm" variant="ghost" className="px-2">
            <Link to="/workbook">← Back</Link>
          </Button>
          <span className="text-sm font-semibold text-fg">{wb.workbookName}</span>
          <Badge variant="info" size="md">{wb.accessLevel}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" type="button"
            onClick={() => void f.onExportWorkbook()} disabled={f.exporting}>
            <Download size={14} />
            {f.exporting ? "Exporting..." : "Export"}
          </Button>
          <Button size="sm" variant="ghost" type="button"
            onClick={() => f.setSidebarOpen(true)}>
            <Settings2 size={14} />
            Metadata
          </Button>
          <Button size="sm" type="button"
            onClick={() => void f.onSaveSnapshot()}
            disabled={!f.hasChanges || f.saving}>
            <Save size={14} />
            {f.saving ? "Saving..." : "Save snapshot"}
          </Button>
        </div>
      </div>

      {/* Validation error banner — shown when save is blocked by invalid data */}
      {f.validationMessages.length > 0 && (
        <div
          role="alert"
          className="mx-4 rounded-xl border border-danger/35 bg-danger/10 px-3.5 py-3 text-xs text-danger"
        >
          <p className="m-0 font-semibold">Validation issues</p>
          <ul className="mb-0 mt-2 list-disc pl-4">
            {f.validationMessages.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Full-width tabs */}
      <Tabs defaultValue="hotel" className="flex flex-1 flex-col overflow-hidden">
        <TabsList className="flex flex-shrink-0 flex-wrap gap-1 border-b border-line bg-transparent px-4 py-2">
          <TabsTrigger value="hotel">Hotel</TabsTrigger>
          <TabsTrigger value="isp">ISP</TabsTrigger>
          <TabsTrigger value="equipment">
            Equipment{f.validationErrorCountBySection.equipment > 0 ? ` (${f.validationErrorCountBySection.equipment})` : ""}
          </TabsTrigger>
          <TabsTrigger value="switch-port-map">
            Switch Port Map{f.validationErrorCountBySection.switchPortMap > 0 ? ` (${f.validationErrorCountBySection.switchPortMap})` : ""}
          </TabsTrigger>
          <TabsTrigger value="vlan-legend">
            VLAN Legend{f.validationErrorCountBySection.vlanLegend > 0 ? ` (${f.validationErrorCountBySection.vlanLegend})` : ""}
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-auto px-4 py-4">
          <TabsContent value="hotel" className="mt-0 grid gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hotel-description">Hotel Description</Label>
              <textarea id="hotel-description" aria-label="Hotel Description" value={f.hotelDescription}
                onChange={(e) => f.setHotelDescription(e.target.value)}
                className="min-h-[160px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              />
            </div>
          </TabsContent>

          <TabsContent value="isp" className="mt-0 grid gap-4">
            <WorkbookIspTab f={f} />
          </TabsContent>

          <TabsContent value="equipment" className="mt-0 grid gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="equipment-notes">Notes</Label>
              <textarea id="equipment-notes" aria-label="Notes" value={f.equipmentNotes}
                onChange={(e) => f.setEquipmentNotes(e.target.value)}
                className="min-h-[80px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="ghost"
                onClick={() => f.setEquipmentList((prev) => [...prev, newEquipmentRow()])}>
                Add equipment row
              </Button>
            </div>
            <HandsontableGrid data={padRows(f.equipmentList, newEquipmentRow)} columns={EQUIPMENT_COLUMNS}
              onDataChange={(data) => f.setEquipmentList(trimTrailingEmptyRows(data))} tableHeight={TABLE_HEIGHT} />
          </TabsContent>

          <TabsContent value="switch-port-map" className="mt-0 grid gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="switch-port-map-notes">Notes</Label>
              <textarea id="switch-port-map-notes" aria-label="Notes" value={f.switchPortMapNotes}
                onChange={(e) => f.setSwitchPortMapNotes(e.target.value)}
                className="min-h-[80px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              />
            </div>
            <HandsontableGrid data={padRows(f.switchPortMap, newSwitchPortRow)} columns={SWITCH_PORT_COLUMNS}
              onDataChange={(data) => f.setSwitchPortMap(trimTrailingEmptyRows(data))} tableHeight={TABLE_HEIGHT} />
          </TabsContent>

          <TabsContent value="vlan-legend" className="mt-0 grid gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vlan-legend-notes">Notes</Label>
              <textarea id="vlan-legend-notes" aria-label="Notes" value={f.vlanLegendNotes}
                onChange={(e) => f.setVlanLegendNotes(e.target.value)}
                className="min-h-[80px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="ghost"
                onClick={() => f.setVlanLegend((prev) => [...prev, newVlanLegendRow()])}>
                Add VLAN row
              </Button>
              <Button type="button" size="sm" variant="ghost"
                onClick={() => f.setVlanLegend((prev) => prev.slice(0, -1))}
                disabled={f.vlanLegend.length === 0}>
                Remove last VLAN row
              </Button>
            </div>
            <HandsontableGrid data={padRows(f.vlanLegend, newVlanLegendRow)} columns={VLAN_LEGEND_COLUMNS}
              onDataChange={(data) => f.setVlanLegend(trimTrailingEmptyRows(data))} tableHeight={TABLE_HEIGHT} />
          </TabsContent>
        </div>
      </Tabs>

      <WorkbookSidebar
        isOpen={f.sidebarOpen}
        onClose={() => f.setSidebarOpen(false)}
        record={record}
        workbook={wb}
        activeSnapshot={activeSnapshot}
        workbookName={f.workbookName}
        setWorkbookName={f.setWorkbookName}
        propertyName={f.propertyName}
        setPropertyName={f.setPropertyName}
        status={f.status}
        setStatus={f.setStatus}
        author={f.author}
        setAuthor={f.setAuthor}
        description={f.description}
        setDescription={f.setDescription}
        hasChanges={f.hasChanges}
        saving={f.saving}
        validationMessages={f.validationMessages}
        onSave={() => void f.onSaveSnapshot()}
        selectedSnapshotId={f.selectedSnapshotId}
        setSelectedSnapshotId={f.setSelectedSnapshotId}
        rollbackConfirmed={f.rollbackConfirmed}
        setRollbackConfirmed={f.setRollbackConfirmed}
        rollbackDiff={f.rollbackDiff}
        rollingBack={f.rollingBack}
        onRollback={() => void f.onRollback()}
      />
    </div>
  );
}
